/** Empty state shown when no document tab is open.
 *  Layout: left-anchored hub (anti-center bias), quiet brand accent on the
 *  primary CTA, three-step pipeline hint, secondary actions inline.
 */

import { useNavigate } from 'react-router-dom'
import { Plus, Search, Sparkles, UploadCloud, ArrowRight, Layers, GitBranch } from 'lucide-react'
import { useAppStore } from '../../stores/app-store'
import { useCreateDocument } from '../../hooks/use-documents'

const TRANSITION = 'transition-[background-color,color,transform,box-shadow,border-color] duration-200 ease-out'

export function WelcomeScreen() {
  const { theme, openTab, setActiveTab, toggleCommandPalette } = useAppStore()
  const createDocument = useCreateDocument()
  const navigate = useNavigate()
  const isDark = theme === 'dark'

  const handleNewDoc = async () => {
    try {
      const doc = await createDocument.mutateAsync({ title: 'Untitled' })
      const tabId = `tab-${doc.id}`
      openTab({ id: tabId, documentId: doc.id, title: doc.title })
      setActiveTab(tabId)
      navigate(`/doc/${doc.slug}`)
    } catch (err) {
      console.error('Failed to create document:', err)
    }
  }

  return (
    <div
      className={`relative flex h-full overflow-hidden ${
        isDark ? 'bg-surface-0 text-neutral-100' : 'bg-neutral-50 text-neutral-900'
      }`}
    >
      {/* Quiet edge accent — single soft glow at top-left, not centered halo */}
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute -left-32 -top-32 h-[420px] w-[420px] rounded-full blur-[120px] ${
          isDark ? 'bg-brand-500/[0.08]' : 'bg-brand-500/[0.06]'
        }`}
      />

      {/* Left-anchored content rail (anti-center bias). Right side intentional whitespace. */}
      <div className="relative z-10 mx-auto flex w-full max-w-2xl flex-col justify-center gap-9 px-8 py-16 md:max-w-3xl md:gap-10 lg:mx-0 lg:ml-[max(7vw,4rem)] lg:max-w-xl">
        {/* Tag line */}
        <div className="flex items-center gap-2">
          <span className={`h-1.5 w-1.5 rounded-full ${isDark ? 'bg-brand-400' : 'bg-brand-500'}`} aria-hidden="true" />
          <span className={`text-[11px] font-medium uppercase tracking-[0.14em] ${isDark ? 'text-brand-400' : 'text-brand-600'}`}>
            Start here
          </span>
        </div>

        {/* Heading + positioning copy */}
        <div className="flex flex-col gap-3">
          <h1 className={`text-3xl font-semibold tracking-tight md:text-[2.5rem] md:leading-[1.1] ${
            isDark ? 'text-neutral-50' : 'text-neutral-900'
          }`}>
            Knowledge ingest, ready to <span className={isDark ? 'text-brand-300' : 'text-brand-600'}>review</span>.
          </h1>
          <p className={`max-w-[58ch] text-sm leading-relaxed md:text-[15px] ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>
            Start with ingest. Storage keeps raw uploads and extracted files, then reviewed batches
            commit into the wiki and document graph for humans and AI agents to query.
          </p>
        </div>

        {/* Primary CTA — quiet card with subtle ring and hover lift */}
        <button
          onClick={() => navigate('/ingest/upload')}
          className={`group relative flex items-center gap-4 rounded-2xl px-5 py-4 text-left ${TRANSITION} active:scale-[0.99] ${
            isDark
              ? 'border border-white/[0.06] bg-surface-1/60 hover:border-brand-500/30 hover:bg-surface-1'
              : 'border border-neutral-200 bg-white hover:border-brand-500/30 hover:bg-white'
          }`}
        >
          <span
            aria-hidden="true"
            className={`pointer-events-none absolute inset-y-0 left-0 w-[3px] rounded-l-2xl ${TRANSITION} ${
              isDark ? 'bg-brand-400/0 group-hover:bg-brand-400' : 'bg-brand-500/0 group-hover:bg-brand-500'
            }`}
          />
          <span
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
              isDark ? 'bg-brand-500/10 text-brand-300' : 'bg-brand-500/[0.08] text-brand-600'
            }`}
          >
            <UploadCloud className="h-[18px] w-[18px]" aria-hidden="true" strokeWidth={2} />
          </span>
          <span className="min-w-0 flex-1">
            <span className={`block text-[15px] font-medium ${isDark ? 'text-neutral-50' : 'text-neutral-900'}`}>
              Ingest your first batch
            </span>
            <span className={`mt-0.5 block text-xs ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>
              Upload files first. Review comes next, then commit the approved knowledge into the workspace.
            </span>
          </span>
          <ArrowRight
            aria-hidden="true"
            className={`h-4 w-4 shrink-0 ${TRANSITION} group-hover:translate-x-0.5 ${
              isDark ? 'text-neutral-500 group-hover:text-brand-300' : 'text-neutral-400 group-hover:text-brand-600'
            }`}
            strokeWidth={2}
          />
        </button>

        {/* 3-step pipeline hint — replaces generic 'open from sidebar' line */}
        <ol className={`grid grid-cols-3 gap-px overflow-hidden rounded-xl border ${
          isDark ? 'border-white/[0.06] bg-white/[0.02]' : 'border-neutral-200 bg-neutral-100/50'
        }`}>
          {[
            { step: '01', icon: UploadCloud, label: 'Drop', body: 'Files land in a batch.' },
            { step: '02', icon: Layers, label: 'Classify', body: 'OCR + cluster.' },
            { step: '03', icon: GitBranch, label: 'Commit', body: 'Write approved docs to the wiki + graph.' },
          ].map(({ step, icon: Icon, label, body }) => (
            <li
              key={step}
              className={`flex flex-col gap-2 px-4 py-3.5 ${
                isDark ? 'bg-surface-0' : 'bg-white'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className={`font-mono text-[10px] tabular-nums ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`}>
                  {step}
                </span>
                <Icon className={`h-3.5 w-3.5 ${isDark ? 'text-neutral-500' : 'text-neutral-500'}`} aria-hidden="true" strokeWidth={2} />
                <span className={`text-xs font-medium ${isDark ? 'text-neutral-200' : 'text-neutral-800'}`}>
                  {label}
                </span>
              </div>
              <p className={`text-[11px] leading-snug ${isDark ? 'text-neutral-500' : 'text-neutral-500'}`}>
                {body}
              </p>
            </li>
          ))}
        </ol>

        {/* Secondary actions — quiet inline chips, not button-y */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
          <button
            onClick={handleNewDoc}
            disabled={createDocument.isPending}
            className={`inline-flex items-center gap-1.5 ${TRANSITION} disabled:opacity-50 active:translate-y-px ${
              isDark
                ? 'text-neutral-300 hover:text-brand-300'
                : 'text-neutral-700 hover:text-brand-600'
            }`}
          >
            <Plus className="h-3.5 w-3.5" aria-hidden="true" strokeWidth={2} />
            <span>New document</span>
          </button>
          <span className={isDark ? 'text-neutral-700' : 'text-neutral-300'} aria-hidden="true">·</span>
          <button
            onClick={toggleCommandPalette}
            className={`inline-flex items-center gap-1.5 ${TRANSITION} active:translate-y-px ${
              isDark
                ? 'text-neutral-300 hover:text-brand-300'
                : 'text-neutral-700 hover:text-brand-600'
            }`}
          >
            <Search className="h-3.5 w-3.5" aria-hidden="true" strokeWidth={2} />
            <span>Search docs</span>
          </button>
          <span className={isDark ? 'text-neutral-700' : 'text-neutral-300'} aria-hidden="true">·</span>
          <span className={`hidden items-center gap-1.5 md:inline-flex ${isDark ? 'text-neutral-500' : 'text-neutral-500'}`}>
            <Sparkles className="h-3 w-3" aria-hidden="true" strokeWidth={2} />
            <span>
              Press{' '}
              <kbd className={`rounded px-1.5 py-0.5 font-mono text-[10px] ${
                isDark ? 'bg-surface-2 text-neutral-300' : 'bg-neutral-100 text-neutral-700'
              }`}>
                Ctrl+K
              </kbd>{' '}
              to open command palette
            </span>
          </span>
        </div>
      </div>
    </div>
  )
}
